import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  reconciliationApi,
  type DriftFinding,
  type ReconciliationReport,
  type ReconciliationRunSummary,
} from '@/lib/api';
import { cn } from '@/lib/utils';
import { CheckCircle } from '@phosphor-icons/react';

const KIND_LABEL: Record<DriftFinding['kind'], { label: string; color: string; help: string }> = {
  processed_receipt_missing_outcome: {
    label: 'Receipt processed, outcome missing',
    color: 'bg-red-100 text-red-700',
    help: 'Outcome webhook was marked processed but the case does not have outcome set. Indicates a failed updateOutcome call.',
  },
  case_outcome_without_receipt: {
    label: 'Outcome without receipt',
    color: 'bg-amber-100 text-amber-700',
    help: 'Case has a terminal outcome but no processed receipt or manual outcome event backs it. Indicates an outcome write that bypassed the audit path.',
  },
  skipped_orphan: {
    label: 'Orphan (replayable)',
    color: 'bg-blue-100 text-blue-700',
    help: 'Outcome receipt arrived before the case existed. Replay from the Webhook Receipts page once the case lands.',
  },
  stale_pending_receipt: {
    label: 'Stale pending',
    color: 'bg-slate-200 text-slate-700',
    help: 'Receipt stuck in received/pending status for more than 1 hour. Processor likely crashed mid-flight.',
  },
};

function timeAgo(unixSec: number): string {
  const diff = Math.floor(Date.now() / 1000 - unixSec);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function CountChip({ count, label, color }: { count: number; label: string; color: string }) {
  return (
    <div className={cn('rounded-lg px-3 py-2 flex items-center gap-2', color)}>
      <span className="text-lg font-bold font-mono tabular-nums leading-none">{count}</span>
      <span className="text-[11px] font-semibold uppercase tracking-wide">{label}</span>
    </div>
  );
}

function RunRow({
  run,
  selected,
  onClick,
}: {
  run: ReconciliationRunSummary;
  selected: boolean;
  onClick: () => void;
}) {
  const drift = run.counts.totalDrift;
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left rounded-lg border p-3 transition',
        selected ? 'border-primary bg-primary/5 shadow-sm' : 'border-slate-200 bg-white hover:bg-slate-50',
      )}
    >
      <div className="flex items-baseline gap-2">
        <span className={cn('text-base font-bold font-mono tabular-nums', drift === 0 ? 'text-emerald-600' : 'text-red-600')}>
          {drift}
        </span>
        <span className="text-[11px] text-slate-500 uppercase tracking-wide">drift findings</span>
        <span className="flex-1" />
        <span className="text-[11px] text-slate-400 font-mono">{timeAgo(run.startedAt)}</span>
      </div>
      <p className="text-[11px] text-slate-400 mt-1 font-mono">
        {run.companyId ?? 'all tenants'} • window from {run.windowStart.slice(0, 10)}
      </p>
      <p className="text-[11px] text-slate-400 mt-1">
        scanned {run.counts.receiptsScanned} receipts • {run.counts.casesScanned} cases
      </p>
    </button>
  );
}

function FindingsTable({ report }: { report: ReconciliationReport }) {
  if (report.findings.length === 0) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-6 text-center">
        <CheckCircle size={28} weight="regular" className="text-emerald-500 mx-auto" />
        <p className="mt-2 text-[13px] font-semibold text-emerald-800">No drift detected</p>
        <p className="text-[11px] text-emerald-700">All receipts and case outcomes reconcile cleanly in this window.</p>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <table className="w-full text-[12px]">
        <thead className="bg-slate-50/70">
          <tr className="border-b border-slate-100">
            <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400 text-left">Kind</th>
            <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400 text-left">Case / Receipt</th>
            <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400 text-left">Tenant</th>
            <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400 text-left">Detail</th>
            <th className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400 text-right">When</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {report.findings.map((f, i) => {
            const meta = KIND_LABEL[f.kind];
            return (
              <tr key={i} className="hover:bg-slate-50/60 transition">
                <td className="px-4 py-2.5">
                  <span title={meta.help} className={cn('inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold', meta.color)}>
                    {meta.label}
                  </span>
                </td>
                <td className="px-4 py-2.5 font-mono text-[11px] text-slate-600">
                  {f.caseId && <div>{f.caseId}</div>}
                  {f.receiptId && <div className="text-slate-400">receipt: {f.receiptId.slice(0, 12)}…</div>}
                </td>
                <td className="px-4 py-2.5 font-mono text-[11px] text-slate-600">{f.companyId ?? '—'}</td>
                <td className="px-4 py-2.5 text-slate-700">
                  {f.detail}
                  {(f.platform || f.eventType) && (
                    <span className="block text-[10px] text-slate-400 font-mono mt-0.5">
                      {f.platform} {f.eventType ? `· ${f.eventType}` : ''}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right font-mono text-[11px] text-slate-400">
                  {f.detectedAtSourceTime ? timeAgo(f.detectedAtSourceTime) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {report.findingsTruncated && (
        <div className="px-4 py-2 bg-amber-50 text-[11px] text-amber-700 border-t border-amber-200">
          Showing first 500 findings — full set is re-derivable by re-running.
        </div>
      )}
    </div>
  );
}

function ReconciliationPage() {
  const queryClient = useQueryClient();
  const [companyId, setCompanyId] = useState<string>('');
  const [windowDays, setWindowDays] = useState<number>(30);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const runsQuery = useQuery({
    queryKey: ['admin-reconciliation-runs', companyId],
    queryFn: () => reconciliationApi.listRuns(companyId || undefined, 30),
  });

  const detailQuery = useQuery({
    queryKey: ['admin-reconciliation-run', selectedRunId],
    queryFn: () => reconciliationApi.getRun(selectedRunId as string),
    enabled: !!selectedRunId,
  });

  const triggerMutation = useMutation({
    mutationFn: () => reconciliationApi.trigger(companyId || undefined, windowDays),
    onSuccess: (response) => {
      if (response.data) {
        setSelectedRunId(response.data.runId);
      }
      queryClient.invalidateQueries({ queryKey: ['admin-reconciliation-runs'] });
    },
  });

  const runs = runsQuery.data?.data?.runs ?? [];
  const detail = detailQuery.data?.data;

  // Auto-select most recent run if nothing is selected
  if (!selectedRunId && runs.length > 0) {
    setSelectedRunId(runs[0].runId);
  }

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Reconciliation</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            Detects drift between webhook receipts and case outcomes — surfaces silent pipeline failures.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Filter by companyId (blank = all)"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            className="rounded border border-slate-200 px-3 py-1.5 text-[12px] font-mono w-56"
          />
          <input
            type="number"
            min={1}
            max={365}
            value={windowDays}
            onChange={(e) => setWindowDays(Math.max(1, Math.min(365, parseInt(e.target.value, 10) || 30)))}
            className="rounded border border-slate-200 px-3 py-1.5 text-[12px] font-mono w-20"
            title="Window (days)"
          />
          <button
            onClick={() => triggerMutation.mutate()}
            disabled={triggerMutation.isPending}
            className={cn(
              'rounded px-3 py-1.5 text-[12px] font-semibold transition',
              triggerMutation.isPending
                ? 'bg-slate-200 text-slate-400 cursor-wait'
                : 'bg-slate-900 text-white hover:bg-slate-800',
            )}
          >
            {triggerMutation.isPending ? 'Running…' : 'Run Now'}
          </button>
        </div>
      </div>

      {triggerMutation.error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-[12px] text-red-700">
          {(triggerMutation.error as Error).message ?? 'Run failed'}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Runs list */}
        <div className="lg:col-span-1 space-y-2">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.08em] text-slate-400 px-1">Recent Runs</h2>
          {runsQuery.isLoading && <p className="text-[12px] text-slate-400 px-1">Loading…</p>}
          {!runsQuery.isLoading && runs.length === 0 && (
            <p className="text-[12px] text-slate-400 px-1">No runs yet. Click "Run Now" to seed the first one.</p>
          )}
          <div className="space-y-2">
            {runs.map((r) => (
              <RunRow
                key={r.runId}
                run={r}
                selected={selectedRunId === r.runId}
                onClick={() => setSelectedRunId(r.runId)}
              />
            ))}
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-3 space-y-4">
          {!detail && !detailQuery.isLoading && (
            <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-[12px] text-slate-400">
              Select a run on the left to see drift findings.
            </div>
          )}
          {detail && (
            <>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-baseline gap-3 mb-3">
                  <h2 className="text-[14px] font-bold text-slate-800 font-mono">{detail.runId}</h2>
                  <span className="text-[11px] text-slate-400">
                    started {new Date(detail.startedAt * 1000).toLocaleString()}
                  </span>
                  <span className="flex-1" />
                  <span className="text-[11px] text-slate-400">{detail.companyId ?? 'all tenants'}</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <CountChip count={detail.counts.totalDrift} label="Total Drift" color={detail.counts.totalDrift === 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'} />
                  <CountChip count={detail.counts.processedReceiptMissingOutcome} label="Missing Outcome" color="bg-red-50 text-red-700" />
                  <CountChip count={detail.counts.caseOutcomeWithoutReceipt} label="No Receipt" color="bg-amber-50 text-amber-700" />
                  <CountChip count={detail.counts.skippedOrphans} label="Orphans" color="bg-blue-50 text-blue-700" />
                  <CountChip count={detail.counts.stalePending} label="Stale Pending" color="bg-slate-100 text-slate-700" />
                  <CountChip count={detail.counts.receiptsScanned} label="Receipts Scanned" color="bg-slate-100 text-slate-600" />
                  <CountChip count={detail.counts.casesScanned} label="Cases Scanned" color="bg-slate-100 text-slate-600" />
                </div>
              </div>
              <FindingsTable report={detail} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/reconciliation')({
  component: ReconciliationPage,
});
